/**
 * Outbound-credentials HTTP service (PX-W6.5) — the org-admin CRUD surface.
 *
 * ⚠ `subject` is a QUERY param on every keyed route; a BLANK subject means
 * the app-only grant (the routes default `subject: String ?= ""` and the
 * store collapses blank → null). The client therefore OMITS the param when
 * the subject is null rather than sending an empty string, so the two forms
 * cannot drift.
 *
 * ⚠ Rotation rule surfaced as helper text: a PUT body whose
 * `tenantExternalId` is blank KEEPS the stored value
 * (`OutboundCredentialApplicationService.tenantToStore` returns the existing
 * row's tenant when the requested one is null).
 */

import { apiClient } from '@/services'

import type { OutboundCredentialInput, OutboundCredentialMetadata } from '../types'

/** `?subject=` only when the grant is subject-scoped; absent for app-only. */
const subjectParams = (
  subject: string | null
): { params: { subject: string } } | undefined =>
  subject === null || subject === '' ? undefined : { params: { subject } }

export const credentialsService = {
  /** Every credential the organisation holds (the allow-list records). */
  list(): Promise<OutboundCredentialMetadata[]> {
    return apiClient.get<OutboundCredentialMetadata[]>('/{orgId}/outbound-credentials')
  },

  /**
   * Create or rotate one credential. Returns the stored row's metadata
   * (never the secret — the response type has nowhere to put it).
   */
  put(
    provider: string,
    subject: string | null,
    input: OutboundCredentialInput
  ): Promise<OutboundCredentialMetadata> {
    return apiClient.put<OutboundCredentialMetadata>(
      `/{orgId}/outbound-credentials/${encodeURIComponent(provider)}`,
      input,
      subjectParams(subject)
    )
  },

  /**
   * Revoke (delete) one credential. 404 when already absent — the caller
   * treats that as the deleted state, not a hard error.
   */
  revoke(provider: string, subject: string | null): Promise<OutboundCredentialMetadata> {
    return apiClient.delete<OutboundCredentialMetadata>(
      `/{orgId}/outbound-credentials/${encodeURIComponent(provider)}`,
      subjectParams(subject)
    )
  },

  /**
   * Run the health check for one credential and return the row with the
   * fresh `lastHealth` stamp.
   */
  healthCheck(
    provider: string,
    subject: string | null
  ): Promise<OutboundCredentialMetadata> {
    return apiClient.post<OutboundCredentialMetadata>(
      `/{orgId}/outbound-credentials/${encodeURIComponent(provider)}/health-check`,
      undefined,
      subjectParams(subject)
    )
  }
}
